import { Avatar, AvatarFallback, AvatarImage } from '@/components/base/avatar'
import { Button } from '@/components/base/button'
import { ScrollArea } from '@/components/base/scroll-area'
import { Icon } from '@/components/common/icons/Icon'
import {
  UnorderedList,
  UnorderedListBottom,
  UnorderedListItem,
} from '@/components/UnorderedList'
import type { Resource } from '@/helpers/types'
import { cn } from '@/helpers/utils'
import { useState } from 'react'

interface DirectoryListProps {
  onClickResource: (resource: Resource) => void
  resources: Resource[]
  searchTerm: string
}

export const DirectoryList = ({
  onClickResource,
  resources = [],
  searchTerm = '',
}: DirectoryListProps) => {
  const [selectedResourceId, setSelectedResourceId] = useState('')

  const handleClickShowResource = (resource: Resource) => {
    onClickResource(resource)
    setSelectedResourceId(resource.id)
  }

  return (
    <section className="flex h-full overflow-y-hidden">
      <ScrollArea
        // NOTE: the w-full and margin-right make the scroll bar tight to the right edge
        className="mr-1.5 w-full"
      >
        <UnorderedList aria-label="Directory Resources">
          {resources.map(resource => (
            <UnorderedListItem
              className={cn(
                'relative flex min-h-14 border-none before:absolute before:inset-0 before:w-2 before:transition-colors hover:bg-neutral-light hover:text-primary-light',
                selectedResourceId === resource.id
                  ? 'before:bg-primary-light'
                  : 'before:bg-transparent',
              )}
              key={resource.id}
            >
              <Button
                className="flex grow gap-x-4 whitespace-normal p-0 text-left text-primary"
                onClick={() => handleClickShowResource(resource)}
                variant="link"
              >
                {/* TODO: resource type needed or no? */}
                {/* <ResourceTypeIcon
                className="shrink-0"
                resourceType={resource.type}
              /> */}
                <Avatar className="mb-1 h-10 w-10 shrink-0" role="presentation">
                  <AvatarImage
                    alt={resource.displayName}
                    src={resource.coverUrl}
                  />
                  <AvatarFallback className="bg-breeze uppercase text-deep-sea">
                    {resource.displayName.slice(0, 1)}
                  </AvatarFallback>
                </Avatar>
                <span className="line-clamp-2 flex-grow">
                  {resource.displayName}
                </span>
              </Button>
            </UnorderedListItem>
          ))}
          {!resources.length && (
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center gap-x-2">
                <Icon tag="warning" />
                <h3 className="text-xl font-semibold">Address not found</h3>
              </div>
              <p className="flex flex-col items-center p-5 font-montserrat text-sm">
                <span>No results found for</span>
                <span className="py-2 font-semibold italic"> {searchTerm}</span>
                <span> in the directory list.</span>
              </p>
            </div>
          )}
          <UnorderedListBottom>End of List</UnorderedListBottom>
        </UnorderedList>
      </ScrollArea>
    </section>
  )
}
